import { Bell, Building2, CalendarDays, FileSignature, FolderOpen, Heart, MessageCircle, Plus, Receipt, Scale, TrendingUp, User } from "lucide-react";
import { cn } from "@/utils/cn";

/**
 * Icons and color pairs of the setup wizard. The API names them (`icon`,
 * `tone`); this file is the only place that turns a name into pixels.
 */

export const TONES = {
  violet: "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  rose: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  amber: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  green: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  blue: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  teal: "bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300",
  gray: "bg-black/[.04] text-muted-foreground dark:bg-white/[.06]",
};

/** Numbered circles of an agents list (result screen, "Vamos criar"), one color per position. */
export const NUMBER_TONES = [
  "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  "bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300",
];

const ICONS = {
  scale: Scale,
  user: User,
  building: Building2,
  trending: TrendingUp,
  heart: Heart,
  chat: MessageCircle,
  folder: FolderOpen,
  calendar: CalendarDays,
  bell: Bell,
  signature: FileSignature,
  receipt: Receipt,
  plus: Plus,
};

/** Round badge with an icon, as on the model and capability cards. */
export function IconBadge({ icon, tone = "gray", size = "md", className }) {
  const Icon = ICONS[icon] || MessageCircle;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        size === "sm" ? "h-8 w-8" : "h-11 w-11",
        TONES[tone] || TONES.gray,
        className,
      )}
    >
      <Icon className={size === "sm" ? "h-4 w-4" : "h-5 w-5"} strokeWidth={1.8} />
    </span>
  );
}

/** Glass card (over the aurora) with an optional footer strip, the wizard's one surface. */
export function WizardCard({ as: Tag = "div", selected = false, muted = false, className, children, ...rest }) {
  return (
    <Tag
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border bg-white/85 text-left shadow-[0_1px_2px_rgba(16,24,40,0.05)] backdrop-blur-xl transition-colors dark:bg-[#0e0e14]/60",
        selected ? "border-foreground ring-1 ring-foreground" : "border-black/[.08] dark:border-white/10",
        muted && "opacity-70",
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function CardFooter({ tone = "neutral", className, children }) {
  return (
    <div
      className={cn(
        "mt-auto flex items-center gap-1.5 border-t px-5 py-2.5 text-[13px]",
        tone === "waiting"
          ? "border-amber-200/70 bg-amber-50 text-amber-800 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200"
          : "border-black/[.05] bg-black/[.015] text-muted-foreground dark:border-white/[.06] dark:bg-white/[.02]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Black pill button, the one primary action per step. */
export function PrimaryButton({ className, ...rest }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex min-h-[48px] items-center justify-center rounded-full bg-foreground px-7 text-[15px] font-semibold text-background transition-opacity disabled:cursor-not-allowed disabled:opacity-40",
        className,
      )}
      {...rest}
    />
  );
}

export function SecondaryButton({ className, ...rest }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex min-h-[44px] items-center justify-center rounded-full border border-black/[.1] bg-white px-5 text-sm font-semibold text-foreground hover:bg-black/[.03] disabled:opacity-40 dark:border-white/15 dark:bg-transparent dark:hover:bg-white/[.06]",
        className,
      )}
      {...rest}
    />
  );
}
